// Form doldurma — bölüm sayaçları, uzun metin sayacı, tekrarlı alan başlıkları kopya TEK
// kaynağı (Finar UX P2 — Ö3/Ö8/İ6/İ8). Satır içi string yazılmaz. Müşteri katılımcı da bu
// metinleri görür: "AI", "üretim", "danışman notu" ifadesi YASAK (e2e spec 09).

export const FORM_FILL_COPY = {
  sectionCounter: {
    /** Görünür sayaç — tüm sorular. */
    answered: (answered: number, total: number): string =>
      `${answered.toString()}/${total.toString()} soru`,
    /** Görünür sayaç — zorunlu sorular. */
    required: (answered: number, total: number): string =>
      `${answered.toString()}/${total.toString()} zorunlu`,
    /** Ekran okuyucu özeti ("6/7 soru · 6/6 zorunlu"). */
    summary: (answered: number, total: number, reqAnswered: number, reqTotal: number): string =>
      reqTotal > 0
        ? `${answered.toString()}/${total.toString()} soru yanıtlandı · ${reqAnswered.toString()}/${reqTotal.toString()} zorunlu`
        : `${answered.toString()}/${total.toString()} soru yanıtlandı`,
  },
  textCounter: {
    /** Sayaç uyarı eşiği: sınıra bu kadar karakter kala görünür olur (2000 → 1800). */
    warnRatio: 0.9,
    /** İkinci duyuru eşiği (2000 → 1950). */
    closeRatio: 0.975,
    count: (len: number, max: number): string =>
      `${len.toLocaleString("tr-TR")} / ${max.toLocaleString("tr-TR")}`,
    nearLimit: (left: number): string => `Sınıra ${left.toLocaleString("tr-TR")} karakter kaldı`,
    atLimit: "Karakter sınırına ulaşıldı — daha fazla yazılamaz",
    /** Ekran okuyucu duyuruları — her bant için SABİT metin (her tuşta okunmaz). */
    announceNear: (max: number): string =>
      `Karakter sınırına yaklaşıldı (en çok ${max.toLocaleString("tr-TR")})`,
    announceClose: (left: number): string =>
      `Sınıra ${left.toLocaleString("tr-TR")} karakterden az kaldı`,
    /** Yapıştırma sığmadığında tarayıcı kesmesini görünür kılar. */
    truncated: (n: number): string =>
      `Yapıştırılan metnin ${n.toLocaleString("tr-TR")} karakteri sınıra sığmadı`,
  },
  /** "Diğer" gibi açıklama isteyen seçeneğin rozeti. */
  customInputBadge: "Açıklayınız",
  repeater: {
    /** Çerçeve öğe adı vermediyse kullanılan genel ad. */
    fallbackItem: "Kayıt",
    itemTitle: (itemLabel: string, idx: number, firstText: string | null): string =>
      firstText
        ? `${itemLabel} ${(idx + 1).toString()} — ${firstText}`
        : `${itemLabel} ${(idx + 1).toString()}`,
    add: (itemLabel: string): string => `+ ${itemLabel} ekle`,
  },
} as const;

export type SectionTone = "empty" | "partial" | "done";

export interface SectionProgressLike {
  answered: number;
  total: number;
  requiredAnswered?: number;
  requiredTotal?: number;
}

/**
 * Ö3 — bölüm durumu tonu. Kısmi bölüm UYARI (amber) değil hata kırmızısı DEĞİL:
 * kırmızı yalnız gerçek eksik/hata içindir (eksik paneli, kaydedilemeyen alan).
 */
export function sectionTone(
  p: SectionProgressLike,
  opts: { isActive?: boolean } = {},
): SectionTone {
  if (p.total > 0 && p.answered === p.total) return "done";
  const reqTotal = p.requiredTotal ?? 0;
  if (reqTotal > 0 && (p.requiredAnswered ?? 0) === reqTotal && !opts.isActive) return "done";
  if (p.answered > 0) return "partial";
  return "empty";
}

export const SECTION_TONE_COLOR: Record<SectionTone, string> = {
  empty: "var(--color-text-3)",
  partial: "var(--color-warning-text)",
  done: "var(--color-success)",
};

/** Ö8 — uzun metin sayacının durumu (sınırın %90'ından itibaren görünür uyarı). */
export function textCounterState(
  len: number,
  max: number,
): { tone: "normal" | "near" | "at"; message: string | null; announce: string } {
  const T = FORM_FILL_COPY.textCounter;
  if (max <= 0) return { tone: "normal", message: null, announce: "" };
  if (len >= max) return { tone: "at", message: T.atLimit, announce: T.atLimit };
  const closeAt = Math.floor(max * T.closeRatio);
  if (len >= Math.floor(max * T.warnRatio)) {
    // Görünür mesaj kalan sayıyı izler; duyuru yalnız bant değişince (90% → 97,5% → sınır).
    const announce = len >= closeAt ? T.announceClose(max - closeAt) : T.announceNear(max);
    return { tone: "near", message: T.nearLimit(max - len), announce };
  }
  return { tone: "normal", message: null, announce: "" };
}

/**
 * İ8 — tekrarlı alan bloğunun başlığı için ilk dolu metin alanı (kısaltılmış).
 * Çerçeve sırasına göre ilk `text` alt alanındaki boş olmayan değer.
 */
export function repeaterFirstText(
  item: Record<string, unknown> | undefined,
  fields: readonly { key: string; type: string }[],
  maxLen = 40,
): string | null {
  if (!item) return null;
  for (const f of fields) {
    if (f.type !== "text") continue;
    const v = item[f.key];
    if (typeof v === "string" && v.trim().length > 0) {
      const t = v.trim();
      return t.length > maxLen ? `${t.slice(0, maxLen - 1)}…` : t;
    }
  }
  return null;
}
